import { useState, useEffect, useCallback, useRef } from "react";
import _ from "lodash";

interface UseScrollPositionProps {
	elementSelector?: string;
	scrollThreshold?: number;
	debounceMs?: number;
}

interface UseScrollPositionReturn {
	hasScrolled: boolean;
}

const DEFAULT_SCROLL_THRESHOLD = 100;
const DEFAULT_DEBOUNCE_MS = 20;

/**
 * Hook to track scroll position and apply class to sidebar
 * Used for the modules list sidebar positioning behavior
 */
export const useScrollPosition = ({
	elementSelector = "#colunaModulos",
	scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
	debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseScrollPositionProps = {}): UseScrollPositionReturn => {
	const [hasScrolled, setHasScrolled] = useState(false);
	const hasScrolledRef = useRef(false);

	const handleScroll = useCallback(
		_.debounce(() => {
			const element = document.querySelector(elementSelector);
			if (!element) return;

			// If already scrolled and still above threshold, don't update
			if (hasScrolledRef.current && window.pageYOffset > scrollThreshold) return;

			if (window.pageYOffset === 0) {
				element.classList.remove("scroll");
				hasScrolledRef.current = false;
				setHasScrolled(false);
			} else {
				element.classList.add("scroll");
				hasScrolledRef.current = true;
				setHasScrolled(true);
			}
		}, debounceMs),
		[elementSelector, scrollThreshold, debounceMs]
	);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);

		return () => {
			window.removeEventListener("scroll", handleScroll);
			handleScroll.cancel();
		};
	}, [handleScroll]);

	return {
		hasScrolled,
	};
};

export type { UseScrollPositionReturn };
